import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Button,
  CircularProgress,
} from '@mui/material';
import ListIcon from '@mui/icons-material/List';
import MapIcon from '@mui/icons-material/Map';
import { useTranslation } from 'react-i18next';
import { useSearchContext } from '@/shared/contexts/SearchContext';
import { OfferSearchResult, SearchFilters } from '../types/search.types';
import { OfferCard } from './OfferCard';
import { MapView } from './MapView';

interface SearchResultsProps {
  readonly results: OfferSearchResult[];
  readonly allResults: OfferSearchResult[];
  readonly isLoading: boolean;
  readonly isLoadingAllResults: boolean;
  readonly onLoadMore: () => void;
  readonly hasMore: boolean;
  readonly isLoadingMore: boolean;
  readonly pagination: {
    currentPage: number;
    totalPages: number;
    total: number;
    hasMore: boolean;
  };
  readonly searchFilters?: SearchFilters;
  readonly onFavoriteUpdate?: (offerId: string, isFavorite: boolean) => void;
}

export function SearchResults({
  results,
  allResults,
  isLoading,
  isLoadingAllResults,
  onLoadMore,
  hasMore,
  isLoadingMore,
  pagination,
  searchFilters,
  onFavoriteUpdate,
}: Readonly<SearchResultsProps>) {
  const { t } = useTranslation();
  const {
    searchContext: { viewMode },
    setViewMode,
  } = useSearchContext();

  return (
    <Box sx={{ mt: 4 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {searchFilters?.keyword?.trim()
            ? t('search.results.titleWithKeyword', {
                count: pagination.total,
                keyword: searchFilters.keyword.trim(),
              })
            : t('search.results.title')}
        </Typography>

        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, newMode) => {
            if (newMode !== null) {
              setViewMode(newMode);
            }
          }}
          size="small"
          data-testid="search-view-toggle"
          sx={{
            '& .MuiToggleButton-root': {
              '&.Mui-selected': {
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              },
            },
          }}
        >
          <ToggleButton value="list" aria-label="list view" data-testid="search-view-list-btn">
            <ListIcon sx={{ mr: 0.5 }} />
            {t('search.results.listView')}
          </ToggleButton>
          <ToggleButton value="map" aria-label="map view" data-testid="search-view-map-btn">
            <MapIcon sx={{ mr: 0.5 }} />
            {t('search.results.mapView')}
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {/* Map View — always mounted to preserve Azure Maps DOM attachment, hidden via CSS when not active */}
      <Box sx={{ display: viewMode === 'map' ? 'block' : 'none' }}>
        <MapView
          offers={allResults}
          isLoading={isLoadingAllResults}
          searchFilters={searchFilters}
          onSwitchToList={() => setViewMode('list')}
        />
      </Box>

      {/* List View */}
      {viewMode === 'list' && (
        <>
          {/* Inline loading spinner — keeps the outer wrapper (and MapView) mounted */}
          {isLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
              <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                {t('search.searching')}
              </Typography>
            </Box>
          ) : results.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 6,
                px: 2,
                backgroundColor: 'grey.50',
                borderRadius: '10px',
              }}
            >
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                {t('search.noResults')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchFilters?.keyword?.trim()
                  ? t('search.noResultsKeywordHint')
                  : t('search.noResultsHint')}
              </Typography>
            </Box>
          ) : (
            <>
              <Box>
                {results.map(offer => (
                  <OfferCard
                    key={offer.id}
                    offer={offer}
                    searchFilters={searchFilters}
                    onFavoriteUpdate={onFavoriteUpdate}
                  />
                ))}
              </Box>

              {/* Load More Button */}
              {hasMore && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={onLoadMore}
                    disabled={isLoadingMore}
                    sx={{
                      minWidth: 200,
                      textTransform: 'none',
                      fontWeight: 600,
                    }}
                  >
                    {isLoadingMore ? (
                      <>
                        <CircularProgress size={20} sx={{ mr: 1, color: 'inherit' }} />
                        {t('search.loading')}
                      </>
                    ) : (
                      t('search.loadMore')
                    )}
                  </Button>
                </Box>
              )}

              {/* No More Results Message */}
              {!hasMore && results.length > 0 && pagination.total > results.length && (
                <Box sx={{ textAlign: 'center', mt: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('search.noMoreResults')}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </>
      )}
    </Box>
  );
}
